import {
  HARDWARE_CHECK_KINDS,
  HARDWARE_CHECK_LABELS,
  ACCEPT_ONLY_HARDWARE_FIELDS,
  applicableHardwareCheckFields,
  applyHardwareCheck,
  applyInverterIsolatorRatings,
  hardwareCheckRow,
  hardwarePhotoRank,
  hardwareReadingValue,
  hardwareValuesEqual,
  isBlankHardwareLeaf,
  isSwitchboardPrompt,
  type DcIsolatorRatingField,
  type FieldHardware,
  type HardwareCheck,
  type HardwareCheckReading,
  type HardwareCheckRequest,
  type HardwareCheckSelection,
  type HardwareKind,
  type HardwarePhotoCrop,
} from '@cer-autofill/shared';

/** Inverters first, then modules, batteries, AC isolators, DC isolators. */
export const AUTO_HARDWARE_CHECK_KIND_ORDER: readonly HardwareKind[] = [
  'inverters',
  'modules',
  'batteries',
  'acIsolators',
  'dcIsolators',
];

/** Enough for a typical single-unit job without spending the whole hourly allowance. */
export const AUTO_HARDWARE_CHECK_CAP = 8;

export const AUTO_HARDWARE_CHECK_CONCURRENCY = 2;

export const CHOOSE_PHOTO_FOR_EACH_UNIT = 'Choose a photo for each unit';

export const NOT_CHECKED_AUTOMATICALLY = 'Not checked automatically — Check photo';

export const PHOTO_READING_CONFLICT = 'Another photo of this unit reads a different value.';

/**
 * Make and model stay a reviewer choice. Battery series and manufacturer are
 * the same kind of identity field, so they stay unticked too.
 */
const IDENTITY_FIELDS = new Set(['make', 'model', 'series', 'manufacturer']);

const CHECKABLE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export type HardwareCheckPhoto = {
  photoId: string;
  promptKey?: string | undefined;
  attachmentSource?: 'manual' | 'dropbox-import' | undefined;
  mimeType: string;
  /** Newer uploads are preferred when several switchboard shots exist. */
  uploadedAt?: string | undefined;
};

export type PlannedHardwareCheck = {
  kind: HardwareKind;
  index: number;
  photoId: string;
  surface?: 'switchboard';
};

export type SkippedHardwareCheck =
  | { reason: 'multiple-rows'; kind: HardwareKind; rowCount: number }
  | { reason: 'over-cap'; kind: HardwareKind; index: number; photoId: string };

export type HardwareReadingTick = {
  field: string;
  label: string;
  reading: HardwareCheckReading | undefined;
  uncertain: boolean;
  matches: boolean;
  fallback: boolean;
  conflict: boolean;
  /** Checkbox starts checked. Identity, fallback, conflict, matches and blanks do not. */
  preTicked: boolean;
  /** A populated serial, capacity or breaker size is kept. The reading is not applied over it. */
  keepsExisting: boolean;
};

export type AutoCheckError = {
  status: number | undefined;
  message: string;
};

/** Same photo filter the manual checker uses: a usable label photo, not a Dropbox import. */
export function isCheckableHardwarePhoto(
  photo: HardwareCheckPhoto,
  kind: HardwareKind,
  index = 0,
  hardware?: Pick<FieldHardware, 'inverters'>,
): boolean {
  return (
    hardwarePhotoRank(photo.promptKey, kind, index, hardware) > 0 &&
    photo.attachmentSource !== 'dropbox-import' &&
    CHECKABLE_MIME_TYPES.includes(photo.mimeType)
  );
}

function bestFallbackPhoto(
  photos: readonly HardwareCheckPhoto[],
  kind: HardwareKind,
  index: number,
  hardware: FieldHardware,
): HardwareCheckPhoto | undefined {
  let best: { photo: HardwareCheckPhoto; rank: number } | undefined;
  for (const photo of photos) {
    if (photo.attachmentSource === 'dropbox-import') continue;
    if (!CHECKABLE_MIME_TYPES.includes(photo.mimeType)) continue;
    if (HARDWARE_CHECK_KINDS[kind].prompts.includes(photo.promptKey ?? '')) continue;
    const rank = hardwarePhotoRank(photo.promptKey, kind, index, hardware);
    if (rank <= 0) continue;
    if (!best || rank > best.rank) best = { photo, rank };
  }
  return best?.photo;
}

/**
 * Photos that map to exactly one row of their kind. Several units of one kind
 * are skipped entirely — prompt keys are shared across units, so a photo cannot
 * be assigned without guessing.
 */
export function planAutoChecks(
  hardware: FieldHardware,
  photos: readonly HardwareCheckPhoto[],
  options?: { cap?: number },
): { checks: PlannedHardwareCheck[]; skipped: SkippedHardwareCheck[] } {
  const cap = Math.max(0, options?.cap ?? AUTO_HARDWARE_CHECK_CAP);
  const checks: PlannedHardwareCheck[] = [];
  const skipped: SkippedHardwareCheck[] = [];
  for (const kind of AUTO_HARDWARE_CHECK_KIND_ORDER) {
    const rows = hardware[kind];
    if (rows.length !== 1) {
      if (rows.length > 1) skipped.push({ reason: 'multiple-rows', kind, rowCount: rows.length });
      continue;
    }
    // Integrated ratings come from the inverter specification label, not test-meter photos.
    if (
      kind === 'dcIsolators' &&
      [undefined, 'Integrated'].includes(hardware.dcIsolators[0]?.location)
    )
      continue;
    const candidates: { item: PlannedHardwareCheck; rank: number }[] = [];
    for (const photo of photos) {
      if (photo.attachmentSource === 'dropbox-import') continue;
      if (!CHECKABLE_MIME_TYPES.includes(photo.mimeType)) continue;
      // Strict label prompts only. Nearby shots and borrowed nameplates are one fallback below.
      const rank = hardwarePhotoRank(photo.promptKey, kind, 0, hardware);
      if (rank < 100) continue;
      if (
        kind === 'dcIsolators' &&
        /^pv-dc-isolator-(before|during|after)$/.test(photo.promptKey ?? '')
      )
        continue;
      candidates.push({ item: { kind, index: 0, photoId: photo.photoId }, rank });
    }
    candidates.sort((a, b) => b.rank - a.rank || a.item.photoId.localeCompare(b.item.photoId));
    for (const { item } of candidates) {
      if (checks.length >= cap) skipped.push({ reason: 'over-cap', ...item });
      else checks.push(item);
    }
    if (rows.length === 1 && !checks.some((item) => item.kind === kind && !item.surface)) {
      const fallback = bestFallbackPhoto(photos, kind, 0, hardware);
      if (fallback) {
        const item: PlannedHardwareCheck = { kind, index: 0, photoId: fallback.photoId };
        if (checks.length >= cap) skipped.push({ reason: 'over-cap', ...item });
        else checks.push(item);
      }
    }
  }
  if (hardware.batteries.length === 1) {
    const switchboards = photos
      .filter(
        (photo) =>
          isSwitchboardPrompt(photo.promptKey) &&
          photo.attachmentSource !== 'dropbox-import' &&
          CHECKABLE_MIME_TYPES.includes(photo.mimeType),
      )
      .slice()
      .sort((a, b) => (b.uploadedAt ?? '').localeCompare(a.uploadedAt ?? ''));
    for (const switchboard of switchboards) {
      if (checks.some((item) => item.photoId === switchboard.photoId && item.surface)) continue;
      const item: PlannedHardwareCheck = {
        kind: 'batteries',
        index: 0,
        photoId: switchboard.photoId,
        surface: 'switchboard',
      };
      if (checks.length >= cap) skipped.push({ reason: 'over-cap', ...item });
      else checks.push(item);
    }
  }
  return { checks, skipped };
}

/** Uploaded and closed reports are refused by the check service; don't start a run. */
export function autoHardwareChecksAllowed(status: string): boolean {
  return status !== 'uploaded' && status !== 'closed';
}

export function autoCheckProgressLabel(done: number, total: number): string {
  return `Checking photos… ${done}/${total}`;
}

export function displayHardwareValue(value: unknown): string {
  if (value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return '—';
}

/** The same fallbacks the table labels, so a fallback cannot be pre-ticked by a different rule. */
export function isHardwareReadingFallback(field: string, reading: HardwareCheckReading): boolean {
  if (field === 'acPowerRatingW' && /apparent|\b[kK]?VA\b/.test(reading.printedText)) return true;
  if (
    field === 'acCurrentNominalA' &&
    /maximum|(?:^|[^a-z])max(?:$|[^a-z])|I[_\s]*AC[_\s]*max/i.test(reading.printedText)
  )
    return true;
  return field === 'dcCurrentMaximumA' && !!reading.inputCurrentsA?.length;
}

export function hardwareReadingMatches(
  field: string,
  reading: HardwareCheckReading,
  row: Record<string, unknown> | undefined,
): boolean {
  let value: unknown;
  try {
    value = hardwareReadingValue(field, reading.value);
  } catch {
    return false;
  }
  const currentValue =
    field === 'serials' && Array.isArray(row?.[field]) ? row[field].slice(0, 1) : row?.[field];
  const current = displayHardwareValue(currentValue);
  const next = displayHardwareValue(value);
  if (field === 'serial' || field === 'serials') return current === next;
  return current.toLowerCase() === next.toLowerCase();
}

function readingValuesDisagree(
  field: string,
  left: HardwareCheckReading,
  right: HardwareCheckReading,
): boolean {
  try {
    const a = displayHardwareValue(hardwareReadingValue(field, left.value));
    const b = displayHardwareValue(hardwareReadingValue(field, right.value));
    if (field === 'serial' || field === 'serials') return a !== b;
    return a.toLowerCase() !== b.toLowerCase();
  } catch {
    return true;
  }
}

export function hardwareReadingTicks(
  result: HardwareCheck,
  row: Record<string, unknown> | undefined,
  otherResultsForRow: readonly HardwareCheck[],
): HardwareReadingTick[] {
  return applicableHardwareCheckFields(result.kind, result.surface).map((field) => {
    const reading = result.readings.find((item) => item.field === field);
    const matches = reading ? hardwareReadingMatches(field, reading, row) : false;
    const fallback = reading ? isHardwareReadingFallback(field, reading) : false;
    const conflict =
      !!reading &&
      otherResultsForRow.some((other) => {
        const theirs = other.readings.find((item) => item.field === field);
        return !!theirs && readingValuesDisagree(field, reading, theirs);
      });
    const keepsExisting =
      !!reading &&
      !matches &&
      ACCEPT_ONLY_HARDWARE_FIELDS.has(field) &&
      !isBlankHardwareLeaf(row?.[field]);
    return {
      field,
      label: HARDWARE_CHECK_LABELS[field] ?? field,
      reading,
      uncertain: result.uncertainFields.includes(field),
      matches,
      fallback,
      conflict,
      keepsExisting,
      preTicked:
        !!reading &&
        !matches &&
        !fallback &&
        !conflict &&
        !keepsExisting &&
        !IDENTITY_FIELDS.has(field) &&
        !ACCEPT_ONLY_HARDWARE_FIELDS.has(field),
    };
  });
}

export type AcceptOnlyReview = {
  resultId: string;
  field: string;
  kind: HardwareKind;
  surface?: 'switchboard';
};

/** An unticked serial, capacity or breaker size. These stay accept-only. */
export function acceptOnlySuggestionNeedsReview(
  tick: HardwareReadingTick,
  accepted: boolean,
): boolean {
  return (
    ACCEPT_ONLY_HARDWARE_FIELDS.has(tick.field) &&
    !!tick.reading &&
    !tick.matches &&
    !tick.keepsExisting &&
    !accepted
  );
}

export function acceptOnlyReviewLabel(count: number): string {
  return `${count} suggestion${count === 1 ? '' : 's'} to review`;
}

export function acceptOnlyReviewsKey(reviews: readonly AcceptOnlyReview[]): string {
  return reviews.map((item) => `${item.resultId}:${item.field}`).join('|');
}

/** Unticked accept-only readings still waiting for the operator. Labelling is not one of them. */
export function pendingAcceptOnlySuggestions(
  results: readonly HardwareCheck[],
  hardware: FieldHardware,
  overrides: Readonly<Record<string, Readonly<Record<string, boolean>>>> = {},
): AcceptOnlyReview[] {
  const pending: AcceptOnlyReview[] = [];
  for (const result of results) {
    const row =
      result.surface === 'switchboard'
        ? {
            circuitBreakerSizeMainSwitchGrid: hardware.circuitBreakerSizeMainSwitchGrid,
            circuitBreakerSizeMainSwitchInverter: hardware.circuitBreakerSizeMainSwitchInverter,
          }
        : hardwareCheckRow(hardware, result.kind, result.index);
    const siblings = results.filter(
      (other) =>
        other.id !== result.id &&
        other.kind === result.kind &&
        other.index === result.index &&
        other.surface === result.surface,
    );
    const chosen = overrides[result.id] ?? {};
    for (const tick of hardwareReadingTicks(result, row, siblings)) {
      if (acceptOnlySuggestionNeedsReview(tick, chosen[tick.field] ?? tick.preTicked))
        pending.push({
          resultId: result.id,
          field: tick.field,
          kind: result.kind,
          ...(result.surface === 'switchboard' ? { surface: 'switchboard' as const } : {}),
        });
    }
  }
  return pending;
}

export function preTickedFields(
  result: HardwareCheck,
  row: Record<string, unknown> | undefined,
  otherResultsForRow: readonly HardwareCheck[] = [],
): string[] {
  return hardwareReadingTicks(result, row, otherResultsForRow)
    .filter((tick) => tick.preTicked)
    .map((tick) => tick.field);
}

/** Fields whose checkbox is on: the pre-tick, unless the reviewer has flipped it. */
export function selectedHardwareFields(
  ticks: readonly HardwareReadingTick[],
  overrides: Readonly<Record<string, boolean>>,
): string[] {
  return ticks
    .filter(
      (tick) =>
        tick.reading &&
        !tick.matches &&
        !tick.keepsExisting &&
        (overrides[tick.field] ?? tick.preTicked),
    )
    .map((tick) => tick.field);
}

/** Hide a photo review the reviewer does not want. Applied readings are tracked separately. */
export function dismissHardwareReview(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? [...ids] : [...ids, id];
}

/** Put back the most recently dismissed review. Applied readings stay hidden. */
export function undoHardwareReviewDismiss(ids: readonly string[]): string[] {
  return ids.slice(0, -1);
}

/**
 * Close the one-photo checker without writing a selection. `undo` is the same
 * result, so a mis-click can open it again.
 */
export function dismissManualHardwareReview<T>(result: T): { open: null; undo: T } {
  return { open: null, undo: result };
}

export function undoManualHardwareReview<T>(undo: T): { open: T; undo: null } {
  return { open: undo, undo: null };
}

/**
 * Reviews still on screen that have at least one ticked reading. Dismissed
 * ids are omitted, so they do not count toward Apply all and are not siblings
 * for the conflict check.
 */
export function applicableHardwareReviews(
  results: readonly HardwareCheck[],
  hardware: FieldHardware,
  options: {
    submittedAt: string;
    photos: readonly { photoId: string; uploadedAt: string }[];
    hiddenIds?: readonly string[];
    overrides?: Readonly<Record<string, Readonly<Record<string, boolean>>>>;
  },
): HardwareCheck[] {
  const hidden = new Set(options.hiddenIds ?? []);
  const visible = results.filter((result) => !hidden.has(result.id));
  return visible.filter((result) => {
    const photo = options.photos.find((item) => item.photoId === result.photoId);
    const current = hardwareCheckResultCurrent(result, {
      hardware,
      submittedAt: options.submittedAt,
      photo: photo ? { photoId: photo.photoId, uploadedAt: photo.uploadedAt } : undefined,
      crop: result.crop,
    });
    if (!current) return false;
    const row = hardwareCheckRow(hardware, result.kind, result.index);
    const siblings = visible.filter(
      (other) =>
        other.id !== result.id && other.kind === result.kind && other.index === result.index,
    );
    return (
      selectedHardwareFields(
        hardwareReadingTicks(result, row, siblings),
        options.overrides?.[result.id] ?? {},
      ).length > 0
    );
  });
}

/**
 * A result stays usable while its own row still matches the snapshot it was
 * read against. Applying a different row must not mark this one out of date.
 */
export function hardwareCheckResultCurrent(
  result: HardwareCheck,
  input: {
    hardware: FieldHardware;
    submittedAt: string;
    photo: { photoId: string; uploadedAt: string } | undefined;
    crop: HardwarePhotoCrop | undefined;
    /** Last row produced by this session's accepted readings; never update this for manual edits. */
    acceptedRow?: Record<string, unknown> | undefined;
  },
): boolean {
  const row = hardwareCheckRow(input.hardware, result.kind, result.index);
  const checked = input.acceptedRow ?? hardwareCheckRow(result.hardware, result.kind, result.index);
  return (
    !!row &&
    !!checked &&
    result.sourceRevision === input.submittedAt &&
    !!input.photo &&
    result.photoId === input.photo.photoId &&
    result.photoUploadedAt === input.photo.uploadedAt &&
    hardwareValuesEqual(checked, row) &&
    hardwareValuesEqual(result.crop, input.crop)
  );
}

export function applyHardwareCheckSelections(
  hardware: FieldHardware,
  applications: readonly {
    result: HardwareCheck;
    fields: readonly string[];
    dcIsolator?: { index: number; fields: readonly DcIsolatorRatingField[] };
  }[],
): { hardware: FieldHardware; selections: HardwareCheckSelection[] } {
  let next = hardware;
  const staged: {
    result: HardwareCheck;
    fields: string[];
    expectedRow: Record<string, unknown>;
    requested?: { index: number; fields: DcIsolatorRatingField[] };
    dcIsolator?: HardwareCheckSelection['dcIsolator'];
  }[] = [];
  for (const application of applications) {
    const requested = [...(application.dcIsolator?.fields ?? [])];
    if (!application.fields.length && requested.length === 0) continue;
    // Isolator-label fields land before any inverter copy, so an explicit label wins.
    if (application.fields.length)
      next = applyHardwareCheck(next, application.result, [...application.fields]);
    const expectedRow =
      application.result.surface === 'switchboard'
        ? {
            circuitBreakerSizeMainSwitchGrid: next.circuitBreakerSizeMainSwitchGrid,
            circuitBreakerSizeMainSwitchInverter: next.circuitBreakerSizeMainSwitchInverter,
          }
        : hardwareCheckRow(next, application.result.kind, application.result.index);
    if (!expectedRow) throw new Error('Hardware row no longer exists');
    staged.push({
      result: application.result,
      fields: [...application.fields],
      expectedRow,
      ...(requested.length && application.dcIsolator
        ? { requested: { index: application.dcIsolator.index, fields: requested } }
        : {}),
    });
  }
  for (const stage of staged) {
    if (!stage.requested) continue;
    const copied = applyInverterIsolatorRatings(
      next,
      stage.result,
      stage.requested.index,
      stage.requested.fields,
    );
    next = copied.hardware;
    const isolator = next.dcIsolators[stage.requested.index];
    if (!copied.applied.length || !isolator) continue;
    stage.dcIsolator = {
      index: stage.requested.index,
      fields: copied.applied,
      expectedRow: isolator,
    };
  }
  // Each selection must describe the isolator after every copy in this batch.
  for (const stage of staged) {
    if (!stage.dcIsolator) continue;
    const isolator = next.dcIsolators[stage.dcIsolator.index];
    if (isolator) stage.dcIsolator = { ...stage.dcIsolator, expectedRow: isolator };
  }
  return {
    hardware: next,
    selections: staged.map((stage) => ({
      id: stage.result.id,
      kind: stage.result.kind,
      index: stage.result.index,
      fields: stage.fields,
      expectedRow: stage.expectedRow,
      ...(stage.result.surface ? { surface: stage.result.surface } : {}),
      ...(stage.dcIsolator ? { dcIsolator: stage.dcIsolator } : {}),
    })),
  };
}

export function hardwareCheckSelectionCurrent(
  hardware: FieldHardware,
  selection: HardwareCheckSelection,
): boolean {
  if (selection.surface === 'switchboard') {
    return Object.keys(selection.expectedRow).every((field) =>
      hardwareValuesEqual(
        (hardware as unknown as Record<string, unknown>)[field],
        selection.expectedRow[field],
      ),
    );
  }
  const isolator = selection.dcIsolator
    ? hardware.dcIsolators[selection.dcIsolator.index]
    : undefined;
  return (
    hardwareValuesEqual(
      hardwareCheckRow(hardware, selection.kind, selection.index),
      selection.expectedRow,
    ) &&
    (!selection.dcIsolator || hardwareValuesEqual(isolator, selection.dcIsolator.expectedRow))
  );
}

/**
 * A later selection for the same row replaces the stored expected row, so the
 * audit sees the row after every accepted reading, not the row after the first.
 */
function remainingIsolatorCopy(
  existing: HardwareCheckSelection['dcIsolator'],
  incoming: HardwareCheckSelection['dcIsolator'],
): HardwareCheckSelection['dcIsolator'] {
  if (!existing || !incoming || existing.index !== incoming.index) return existing;
  const fields = existing.fields.filter((field) => !incoming.fields.includes(field));
  if (!fields.length) return undefined;
  return { index: existing.index, fields, expectedRow: incoming.expectedRow };
}

export function mergeHardwareCheckSelection(
  current: readonly HardwareCheckSelection[],
  selection: HardwareCheckSelection,
): HardwareCheckSelection[] {
  return [
    ...current
      .map((existing) => {
        const sameRow =
          existing.kind === selection.kind &&
          existing.index === selection.index &&
          existing.surface === selection.surface;
        const dcIsolator = remainingIsolatorCopy(existing.dcIsolator, selection.dcIsolator);
        if (!sameRow && dcIsolator === existing.dcIsolator) return existing;
        const next = {
          ...existing,
          ...(sameRow
            ? {
                fields: existing.fields.filter((field) => !selection.fields.includes(field)),
                expectedRow: selection.expectedRow,
              }
            : {}),
          ...(dcIsolator ? { dcIsolator } : {}),
        };
        if (!dcIsolator) delete next.dcIsolator;
        return next;
      })
      .filter((existing) => existing.fields.length > 0 || !!existing.dcIsolator?.fields.length),
    selection,
  ];
}

export function mergeHardwareCheckSelections(
  current: readonly HardwareCheckSelection[],
  incoming: readonly HardwareCheckSelection[],
): HardwareCheckSelection[] {
  return incoming.reduce<HardwareCheckSelection[]>(
    (acc, selection) => mergeHardwareCheckSelection(acc, selection),
    [...current],
  );
}

/** Warn when portal autofill would leave main-switch breakers blank, or breaker ticks wait. */
export function mainSwitchBreakerPortalWarning(
  hardware: Pick<
    FieldHardware,
    'circuitBreakerSizeMainSwitchGrid' | 'circuitBreakerSizeMainSwitchInverter' | 'batteries'
  >,
  pending: readonly AcceptOnlyReview[],
): string | undefined {
  if (hardware.batteries.length === 0) return undefined;
  const blankGrid = isBlankHardwareLeaf(hardware.circuitBreakerSizeMainSwitchGrid);
  const blankInverter = isBlankHardwareLeaf(hardware.circuitBreakerSizeMainSwitchInverter);
  const pendingBreakers = pending.filter(
    (item) =>
      item.surface === 'switchboard' ||
      item.field === 'circuitBreakerSizeMainSwitchGrid' ||
      item.field === 'circuitBreakerSizeMainSwitchInverter',
  );
  if (pendingBreakers.length > 0) {
    return 'Main-switch breaker suggestions are still pending — accept or dismiss them before portal fill.';
  }
  if (blankGrid || blankInverter) {
    return 'Portal fill will leave the main-switch breaker fields blank until sizes are entered or accepted.';
  }
  return undefined;
}

/** Stop the queue on the hourly limit, an unavailable checker, or a stale revision. */
export function autoCheckShouldStop(error: AutoCheckError): boolean {
  if (error.status === 429 || error.status === 503) return true;
  return error.status === 409 && /latest revision|report has changed/i.test(error.message);
}

export function hardwareCheckRequestKey(input: {
  jobId: string;
  submittedAt: string;
  request: HardwareCheckRequest;
}): string {
  return JSON.stringify([
    input.jobId,
    input.submittedAt,
    input.request.kind,
    input.request.index,
    input.request.photoId,
    input.request.surface ?? 'equipment',
    input.request.hardware,
    input.request.crop ?? null,
  ]);
}

const inFlightChecks = new Map<string, Promise<unknown>>();

/** Join a check already started for this snapshot, so a remount does not spend a second request. */
export function shareInFlight<T>(key: string, start: () => Promise<T>): Promise<T> {
  const existing = inFlightChecks.get(key);
  if (existing) return existing as Promise<T>;
  const promise = start().finally(() => {
    if (inFlightChecks.get(key) === promise) inFlightChecks.delete(key);
  });
  inFlightChecks.set(key, promise);
  return promise;
}

export async function runAutoHardwareChecks<T>(options: {
  items: readonly PlannedHardwareCheck[];
  concurrency?: number;
  /**
   * Return false to stop taking further photos, for example when the editor
   * closes. A check already requested still finishes.
   */
  shouldQueue?: () => boolean;
  request: (item: PlannedHardwareCheck) => Promise<T>;
  onResult: (item: PlannedHardwareCheck, result: T) => void;
  onError: (item: PlannedHardwareCheck, error: AutoCheckError) => void;
  onProgress?: (done: number, total: number) => void;
  classifyError?: (error: unknown) => AutoCheckError;
}): Promise<void> {
  const total = options.items.length;
  if (total === 0) {
    options.onProgress?.(0, 0);
    return;
  }
  const concurrency = Math.max(1, options.concurrency ?? AUTO_HARDWARE_CHECK_CONCURRENCY);
  const classify =
    options.classifyError ??
    ((error: unknown): AutoCheckError => ({
      status: undefined,
      message: error instanceof Error ? error.message : 'Photo check failed.',
    }));
  let cursor = 0;
  let done = 0;
  let stopped = false;

  async function worker(): Promise<void> {
    while (!stopped) {
      if (options.shouldQueue && !options.shouldQueue()) {
        stopped = true;
        return;
      }
      const index = cursor;
      cursor += 1;
      if (index >= options.items.length) return;
      const item = options.items[index];
      if (!item) return;
      // Closed between taking a slot and starting the request: don't send it.
      if (options.shouldQueue && !options.shouldQueue()) {
        stopped = true;
        return;
      }
      try {
        const result = await options.request(item);
        options.onResult(item, result);
      } catch (error) {
        const classified = classify(error);
        options.onError(item, classified);
        if (autoCheckShouldStop(classified)) stopped = true;
      } finally {
        done += 1;
        options.onProgress?.(done, total);
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, total) }, () => worker()));
}
